import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Period } from 'dashboard/lib/unixTime';
import { getEffectivePeriod } from 'dashboard/lib/utils/period';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setMetricsFilters } from 'dashboard/redux/actions/metrics-reports';
import {
  ReportsGranularities,
  ReportTemplateToken,
} from 'dashboard/redux/reducers/metrics-reports';
import { getAdjustedFilters } from 'dashboard/redux/selectors/metrics-reports';
import { getReportTags, getSortedReportTemplates } from 'dashboard/redux/selectors/organization';
import { clearMetrics, resetMetricsDateFilter } from 'dashboard/redux/slices/metrics-verification';
import { RootState } from 'dashboard/redux/store';
import {
  loadReportsOverTime,
  loadReportsVolumeSummary,
  setGranularities,
} from 'dashboard/redux/thunk-actions/metrics-reports';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import DashboardReportsAnalyticsView from 'dashboard/views/DashboardReportsAnalyticsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useEffectOnEnvironmentChange from 'lib/hooks/useOnEnvironmentChange';
import useProductFeature from 'lib/hooks/useProductFeature';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const DashboardReportsAnalyticsRoute = (props: PropsFromRedux) => {
  const productFeatureIsDisabled = !useProductFeature(ProductFeatureName.ReportsAnalytics);

  useEffectOnEnvironmentChange(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    props.clearMetrics();
  });

  const { currentEnvironment, filters, templates, lastQueryFilters, granularities } = props;

  // On first creation of the view, lets just take the loaded templates and then set them at an initial value
  useEffect(() => {
    const newGranularities: ReportsGranularities = {};
    // In the event that we get NEW templates, we'll want to make sure that we can set the granularity to Day.
    // Say, we're going from 2 weeks to 1 year and add a bunch of templates. We cannot simply set the granularity to day
    // we'll need to do some math there. I'm not sure if the templates ever will change now, but this is future proofing
    templates.forEach((template) => {
      newGranularities[template.id as ReportTemplateToken] = getEffectivePeriod(Period.Day, [
        filters.createdAtStart,
        filters.createdAtEnd,
      ]);
    });
    props.setGranularities({ granularities: newGranularities });
  }, [templates]);

  const { handleDateUpdate } = useTable({
    filters,
    setFilters: props.setMetricsFilters,
  });

  const handleTemplateFilterUpdate = useCallback((templateIds: string[]) => {
    props.setMetricsFilters({
      templateIds: templateIds,
    });
  }, []);

  const handleTagFilterUpdate = useCallback((tagIds: string[]) => {
    props.setMetricsFilters({
      tagIds: tagIds,
    });
  }, []);

  const { loading: loadingTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Report),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading,
    error,
    callback: loadReportsMetrics,
  } = useApiCallback(
    async (templateIds: string[]) => {
      const overrideFilters = {
        ...filters,
        templateIds: templateIds,
      };
      // When one runs a new load for the verification metrics, we'll need to ensure that their new date range
      // does not require that we reset the granularities for one or many verification types
      const granularityCopy = cloneDeep(granularities);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      Object.keys(granularities).forEach((token: ReportTemplateToken) => {
        granularityCopy[token] = getEffectivePeriod(granularities[token], [
          filters.createdAtStart,
          filters.createdAtEnd,
        ]);
      });
      await Promise.all([
        props.setGranularities({ granularities: granularityCopy }),
        props.loadReportsOverTime({
          currentEnvironment,
          filters: overrideFilters,
          granularities: granularityCopy,
        }),
        props.loadReportsVolumeSummary({ currentEnvironment, filters: overrideFilters }),
      ]);
    },
    [currentEnvironment, filters, granularities],
  );

  const {
    loading: loadingGranular,
    error: errorGranular,
    callback: handleUpdateGranularity,
  } = useApiCallback(
    async (templateIds: ReportTemplateToken[], templateId: ReportTemplateToken, period: Period) => {
      const overrideFilters = {
        ...filters,
        templateIds: templateIds,
      };
      // First, set the recommended Period.
      const granularityCopy = cloneDeep(props.granularities);
      granularityCopy[templateId] = getEffectivePeriod(period, [
        filters.createdAtStart,
        filters.createdAtEnd,
      ]);
      // Also run through all of other granularities to ensure that they are still valid
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      Object.keys(granularityCopy).forEach(function (templateId: ReportTemplateToken) {
        granularityCopy[templateId] = getEffectivePeriod(granularityCopy[templateId], [
          filters.createdAtStart,
          filters.createdAtEnd,
        ]);
      });

      await Promise.all([
        props.setGranularities({ granularities: granularityCopy }),
        props.loadReportsOverTime({
          currentEnvironment,
          filters: overrideFilters,
          granularities: granularityCopy,
        }),
        props.loadReportsVolumeSummary({ currentEnvironment, filters: overrideFilters }),
      ]);
    },
    [currentEnvironment, filters, granularities],
  );

  useApiErrorHandler({
    error,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load reports metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });
  useApiErrorHandler({
    error: errorGranular,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load reports metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const handleDateUpdateOrClearV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      // Ensure we have a date range before we extract dates
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        startDate || endDate
          ? handleDateUpdate({
              startDate,
              endDate,
            })
          : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            props.resetMetricsDateFilter();
      }
    },
    [handleDateUpdate, props.resetMetricsDateFilter],
  );

  return (
    <DashboardReportsAnalyticsView
      templates={templates}
      productFeatureIsDisabled={productFeatureIsDisabled}
      volumeSummary={props.volumeSummary}
      reportsOverTime={props.reportsOverTime}
      granularities={granularities}
      handleUpdateGranularity={handleUpdateGranularity}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
      handleTemplateFilterUpdate={handleTemplateFilterUpdate}
      handleTagFilterUpdate={handleTagFilterUpdate}
      filters={filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      queriedAt={props.queriedAt}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      updatedAt={props.updatedAt}
      tags={props.reportTags}
      loadMetrics={loadReportsMetrics}
      loading={loading || loadingGranular || loadingTags}
      lastQueryFilters={lastQueryFilters}
      error={!!error || !!errorGranular}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  templates: getSortedReportTemplates(state),
  reportTags: getReportTags(state),
  volumeSummary: state.metricsReports.volumeSummary,
  reportsOverTime: state.metricsReports.reportsOverTime,
  granularities: state.metricsReports.granularities,
  filters: getAdjustedFilters(state),
  lastQueryFilters: state.metricsReports.lastQueryFilters,
  queriedAt: state.metricsReports.queriedAt,
  updatedAt: state.metricsReports.updatedAt,
});

const mapDispatchToProps = {
  clearMetrics,
  loadReportsVolumeSummary,
  loadReportsOverTime,
  setGranularities,
  loadTags,
  setMetricsFilters,
  showMessage,
  resetMetricsDateFilter,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardReportsAnalyticsRoute);
